import { useCallback } from 'react';
import { nomAffiche } from '../../shared/format/designation';
import { Bouton } from '../../shared/ui/Bouton';
import type { SonDeVisio, VignetteDeVisio } from './visio';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA VISIO D'APPOINT, POSÉE SUR LE TABLEAU — `CDC-2` F-31, F-32.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Elle est d'appoint, et la surface le dit par sa place.** `DESIGN.md` donne la densité
 * de cet écran : « **minimale, presque nulle** — tout ce qui n'est pas le tracé prend de
 * la place sur le tracé, et la latence du trait est le critère numéro un ». La
 * pré-maquette la pose en **colonne à droite**, en vignettes de la largeur d'une carte,
 * avec le prénom sous l'image et le geste du tuteur à côté. C'est cette forme.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **QUATRE RÈGLES DE CE PANNEAU NE SE DEVINENT PAS À SA LECTURE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **1. AUCUN VOYANT SUR QUI QUE CE SOIT** (DD-07). Ce panneau ne montre **rien** de la
 * liaison des autres : ni qualité, ni latence, ni « en reconnexion ». « Les autres voient
 * qu'elle n'a plus ni image ni son, **sans savoir pourquoi** ; le produit ne désigne
 * aucune cause » (DD-18). Une vignette qui disparaît est tout ce qui paraît, et
 * l'asymétrie est voulue : **sa propre** coupure, elle, est annoncée — par la page, avec
 * les autres bandeaux, « parce que sur sa propre coupure une personne peut agir ».
 *
 * **2. IL N'EXISTE AUCUN GESTE POUR ALLUMER LE FLUX D'UN AUTRE** (DD-16, critère 4).
 * « Personne ne peut allumer le flux de quelqu'un d'autre, jamais, **pas même le
 * tuteur** : couper retire, allumer expose. » Il n'y a donc pas de bouton symétrique à
 * chercher — le contrat n'a pas de route pour cela, et c'est la règle et non un manque.
 *
 * **3. LE TUTEUR COUPE PAR PERSONNE, ET NON PAR VIGNETTE.** Une personne peut avoir son
 * micro ouvert et **aucune image** : elle n'a alors pas de vignette, et son micro doit
 * pouvoir être coupé quand même. Les gestes vivent donc sur la **liste des participants**, que la
 * réponse de l'entrée dans le cours porte déjà — et le serveur traite « rien à couper »
 * comme une réussite, donc l'écran n'a pas à savoir ce qui est publié.
 *
 * **4. LE SON N'EST JAMAIS FILTRÉ.** Au-delà de six flux d'image, « les autres restent
 * présentes **et audibles**, sans image » (Q417) : les vignettes se filtrent, les
 * éléments de son ci-dessous jamais. Ils ne paraissent pas — un lecteur de son visible
 * n'est pas une information — mais ils sont là.
 */
type Props = {
  readonly vignettes: readonly VignetteDeVisio[];
  readonly sons: readonly SonDeVisio[];
  readonly participants: readonly {
    readonly personneId: string;
    readonly prenom: string | null;
    readonly present: boolean;
  }[];
  readonly moiId: string | null;
  readonly estTuteur: boolean;
  readonly microOuvert: boolean;
  readonly cameraAllumee: boolean;
  readonly ecranPartage: boolean;
  readonly onMicro: () => void;
  readonly onCamera: () => void;
  readonly onEcran: () => void;
  readonly onCouper: (
    personneId: string,
    flux: 'micro' | 'camera' | 'ecran',
  ) => void;
  /** Vrai pendant qu'une coupure est en vol : le bouton ne se reclique pas. */
  readonly coupureEnCours: boolean;
};

const NOM_DU_FLUX: Record<'micro' | 'camera' | 'ecran', string> = {
  micro: 'micro',
  camera: 'caméra',
  ecran: 'partage',
};

export function PanneauDeVisio({
  vignettes,
  sons,
  participants,
  moiId,
  estTuteur,
  microOuvert,
  cameraAllumee,
  ecranPartage,
  onMicro,
  onCamera,
  onEcran,
  onCouper,
  coupureEnCours,
}: Props) {
  const prenomDe = useCallback(
    (personneId: string | null): string => {
      // **LES DEUX ABSENCES PRENNENT LE MÊME MOT, ET C'EST LA RÈGLE.** Une vignette
      // sans personne rattachée et une personne anonymisée sont deux causes ; F-46
      // règle 2 veut « une désignation anonyme neutre, LA MÊME POUR TOUS », donc les
      // séparer à l'écran serait exactement les deux vocabulaires que la règle ferme.
      if (personneId === null) return nomAffiche(null);
      const trouve = participants.find(
        (personne) => personne.personneId === personneId,
      );
      // **Un prénom vide n'est pas un défaut** : « vide quand le compte a été
      // anonymisé — le tracé reste alors sans nom », et la même règle vaut ici.
      return nomAffiche(trouve?.prenom);
    },
    [participants],
  );

  const autres = participants.filter(
    (personne) => personne.personneId !== moiId,
  );

  return (
    <aside
      aria-label="Visio du cours"
      // **Aucune largeur et aucune position ici** — anti-pattern 6 : « c'est la page
      // qui espace ses blocs ». La colonne de droite est une décision de mise en page,
      // pas une propriété de ce panneau.
      className="flex flex-col gap-2 rounded-4 bg-fond-surface p-2 shadow-2"
    >
      {/* ── Ses propres flux, et eux seuls s'allument (DD-16) ── */}
      <div
        role="group"
        aria-label="Mes flux"
        className="flex flex-wrap items-center gap-1"
      >
        <Bouton
          variante={microOuvert ? 'faible' : 'douce'}
          taille="petite"
          aria-pressed={microOuvert}
          onClick={onMicro}
        >
          {microOuvert ? 'Micro ouvert' : 'Micro coupé'}
        </Bouton>
        <Bouton
          variante={cameraAllumee ? 'faible' : 'douce'}
          taille="petite"
          aria-pressed={cameraAllumee}
          onClick={onCamera}
        >
          {cameraAllumee ? 'Caméra allumée' : 'Caméra coupée'}
        </Bouton>
        {/* « Tout présent peut partager son écran, tuteur et élèves indifféremment »
            (Q422). Aucune condition de rôle, et il n'y a nulle part où en poser une. */}
        <Bouton
          variante={ecranPartage ? 'faible' : 'douce'}
          taille="petite"
          aria-pressed={ecranPartage}
          onClick={onEcran}
        >
          {ecranPartage ? 'Arrêter le partage' : 'Partager l’écran'}
        </Bouton>
      </div>

      {/* ── Les vignettes, seuil de six déjà appliqué par la feature ── */}
      {vignettes.length === 0 ? (
        <p className="px-1 text-13 text-texte-faible">
          Aucune image pour l’instant.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {vignettes.map((vignette) => (
            <li
              key={vignette.cle}
              className="overflow-hidden rounded-2 border border-bord bg-fond-attenue"
            >
              <video
                // **`ref` plutôt qu'un effet** : la fonction de la feature branche et
                // débranche, et React appelle son retour au démontage. C'est ce qui
                // fait qu'aucun type du fournisseur ne remonte jusqu'ici — D-003.
                ref={(element) =>
                  element === null ? undefined : vignette.brancher(element)
                }
                autoPlay
                playsInline
                // **Son propre flux est muet**, sinon on s'entend avec le délai du
                // réseau. Le son des autres passe par les éléments plus bas.
                muted
                className={[
                  'aspect-video w-full bg-fond-bloc object-cover',
                  // Sa propre image se peint en miroir : c'est ce qu'une personne
                  // attend de se voir, et cela ne vaut pas pour un écran partagé.
                  vignette.estLeMien && !vignette.estUnEcranPartage
                    ? '-scale-x-100'
                    : '',
                ].join(' ')}
              />
              <p className="flex items-center gap-2 px-2 py-1 text-13 text-texte">
                <span className="font-weight-700">
                  {vignette.estLeMien ? 'Vous' : prenomDe(vignette.personneId)}
                </span>
                {vignette.estUnEcranPartage && (
                  <span className="text-texte-faible">écran partagé</span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}

      {/* ── Les gestes du tuteur, par personne et non par vignette ── */}
      {estTuteur && autres.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-bord pt-2">
          {autres.map((personne) => (
            <div key={personne.personneId} className="flex flex-col gap-1">
              <span className="px-1 text-13 text-texte-faible">
                Couper à {nomAffiche(personne.prenom)}
              </span>
              <span className="flex flex-wrap gap-1">
                {(['micro', 'camera', 'ecran'] as const).map((flux) => (
                  <Bouton
                    key={flux}
                    variante="fantome"
                    taille="petite"
                    // Une personne absente n'a aucun flux à couper : le serveur
                    // l'accepterait et écrirait une ligne pour rien.
                    disabled={!personne.present || coupureEnCours}
                    onClick={() => onCouper(personne.personneId, flux)}
                  >
                    {NOM_DU_FLUX[flux]}
                  </Bouton>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Le son, jamais filtré et jamais montré ── */}
      {sons.map((son) => (
        <audio
          key={son.cle}
          ref={(element) =>
            element === null ? undefined : son.brancher(element)
          }
          autoPlay
        />
      ))}
    </aside>
  );
}
